import React, { useEffect, useState } from "react";
import Head from "next/head";
import useSWR from "swr";
import CommunityMap from "@/components-page/communities/CommunityMap";
import * as Communities from "@/components-page/communities";
import { useRouter } from "next/router";
import { MapMarker } from "@/components/Map/Marker";
import { UserLayout } from "@/components/Layout";
import { useTranslation } from "next-i18next";
import { NextPageWithLayout } from "@/pages/_app";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { CommunityInfo, CommunityOverview } from "@/models/community.model";

type Tabs = "all" | "COMMUNITY" | "ASSOCIATION" | "ORGANISATION";

const tabs = [
  { name: "show all", value: "all" },
  { name: "communities", value: "COMMUNITY" },
  { name: "associations", value: "ASSOCIATION" },
  { name: "organisations", value: "ORGANISATION" },
];

const CommunityOverviewPage: NextPageWithLayout = () => {
  const [activeTab, setActiveTab] = useState<Tabs>("all");
  const [activeCommunity, setActiveCommunity] = useState(
    {} as CommunityOverview
  );
  const [communities, setCommunities] = useState([] as CommunityOverview[]);
  const [filterCommunities, setFilterCommunities] = useState(
    [] as CommunityOverview[]
  );
  const [mapLocations, setMapLocations] = useState([] as MapMarker[]);

  const router = useRouter();

  const { data } = useSWR(`/api/communities`);
  const { t } = useTranslation(["pageTitle"]);

  useEffect(() => {
    if (!data) return;

    if (router.query.type !== undefined && router.query.type !== "all") {
      let filterCommunities = [];
      filterCommunities = data.filter(
        (community: CommunityOverview) => community.type === router.query.type
      );
      setActiveTab(router.query.type as Tabs);
      setFilterCommunities(filterCommunities);
    } else {
      setActiveTab("all");
      setFilterCommunities(data);
    }

    setCommunities(data);
    setActiveCommunity(data[0]);
  }, [router.query.type, data]);

  useEffect(() => {
    if (!communities.length) return;

    const createListMarkers = () => {
      const markers = [] as MapMarker[];

      communities.forEach((community) => {
        if (community.geolocation !== null) {
          markers.push({
            ...community.geolocation,
            icon: community.thumbnail,
            name: community.name,
            address: `${community.address?.street} ${community.address?.houseNumber} ${community.address?.postalCode}, ${community.address?.city}`,
          });
        }
      });

      setMapLocations(markers);
    };

    createListMarkers();
  }, [communities]);

  function handleCommunityFilter(filterType: string) {
    let filterCommunities = data;

    if (filterType === "all") {
      setActiveTab("all");
    } else {
      filterCommunities = data.filter(
        (community: CommunityInfo) => community.type === filterType
      );
      setActiveTab(filterType as Tabs);
    }

    setFilterCommunities(filterCommunities);
  }

  function handleActiveCommunity(tag: string) {
    const selectedCommunity = communities.filter(
      (community) => community.tag === tag
    );

    setActiveCommunity(selectedCommunity[0]);
  }

  return (
    <>
      <Head>
        <title>{`${t("communitiesPage")} ${t("baseTitle")}`}</title>
      </Head>
      <div className="space-y-8">
        <div>
          <Communities.CommunityHeader
            tabs={tabs}
            activeTab={activeTab}
            handleFilter={handleCommunityFilter}
          />
          <div className="flex h-full flex-col space-y-4 lg:flex-row lg:space-y-0">
            <Communities.CommunityOverviewMini
              communities={filterCommunities}
              activeCommunity={activeCommunity}
              setActiveCommunity={handleActiveCommunity}
            />
            <Communities.CommunityHighlight community={activeCommunity} />
            <Communities.CommunityOverview
              communities={filterCommunities}
              activeCommunity={activeCommunity}
              setActiveCommunity={handleActiveCommunity}
            />
          </div>
        </div>
        <div>
          <CommunityMap locations={mapLocations} />
        </div>
      </div>
    </>
  );
};

export async function getStaticProps(props: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(props.locale, ["common", "pageTitle"])),
    },
  };
}

CommunityOverviewPage.getLayout = UserLayout;

export default CommunityOverviewPage;
